'use client';

/**
 * AI form bridge — lets the in-app AI assistant fill form fields visibly.
 *
 * Forms register themselves (field list + setter) while mounted; the chat's
 * client tools (list_form_fields / fill_form_field) read and write through
 * the active registration. Values are typed with a typewriter animation so
 * the user SEES the assistant fill the field. The assistant can only fill —
 * submitting/saving always stays with the user.
 *
 * The context value is identity-stable so the chat's tool handlers can hold
 * it in a closure; the active registration is kept in a ref.
 */

import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';

export interface AiFormFieldDescriptor {
  /** Stable field name the model uses with fill_form_field. */
  name: string;
  /** Human-readable label as shown on the form. */
  label: string;
  type: 'text' | 'number' | 'date' | 'select';
  /** Current value snapshot (shown to the model by list_form_fields). */
  value: string | number | null;
  /** Valid choices for select fields — fill with the option value or label. */
  options?: { value: string; label: string }[];
}

export interface AiFormRegistration {
  /** Short form identifier, e.g. 'valuation' or 'sponsorship-event'. */
  formId: string;
  /** One-line description of the form shown to the model. */
  description: string;
  /** Snapshot of the current fields and values. */
  getFields: () => AiFormFieldDescriptor[];
  /** Set a field value. Returns false when the field name is unknown. */
  setField: (name: string, value: string | number) => boolean;
  /**
   * Focus-only form: the assistant may point at fields (focus_form_field)
   * but fill_form_field is rejected — used for settings-style pages where
   * the assistant guides and the user changes values themselves.
   */
  focusOnly?: boolean;
  /**
   * True when the form holds unsaved changes. navigate_to refuses to leave
   * the page while dirty (unless the user explicitly confirmed discarding),
   * so the assistant never throws away the user's in-progress work.
   */
  isDirty?: () => boolean;
}

export interface AiDialogRegistration {
  /** Stable dialog id the model uses with open_dialog, e.g. 'add-item'. */
  dialogId: string;
  /** One-line description of what the dialog does, shown to the model. */
  description: string;
  /**
   * Open the dialog. Returns false when opening is blocked (e.g. demo mode).
   * Once open, the dialog registers its own form so the assistant can fill
   * it — saving/closing always stays with the user.
   */
  open: () => boolean;
}

interface AiFormBridgeValue {
  /** Register the form of the current page. Returns an unregister cleanup. */
  registerForm: (registration: AiFormRegistration) => () => void;
  /**
   * The currently mounted form, or null when the page has none. When several
   * forms are mounted at once (e.g. the Inventory → Settings cards) they are
   * merged into one virtual registration so the chat tools see all fields.
   */
  getActiveForm: () => AiFormRegistration | null;
  /** Register a dialog the assistant may open. Returns an unregister cleanup. */
  registerDialog: (registration: AiDialogRegistration) => () => void;
  /** Dialogs the assistant can open on the current page. */
  getDialogs: () => AiDialogRegistration[];
}

const AiFormBridgeContext = createContext<AiFormBridgeValue>({
  // No-op default so forms can render outside the provider (e.g. tests)
  registerForm: () => () => {},
  getActiveForm: () => null,
  registerDialog: () => () => {},
  getDialogs: () => [],
});

/** Merge sibling registrations into one virtual form (field names are unique). */
function mergeRegistrations(forms: AiFormRegistration[]): AiFormRegistration {
  return {
    formId: forms.map((f) => f.formId).join('+'),
    description: forms.map((f) => `[${f.formId}] ${f.description}`).join(' '),
    getFields: () => forms.flatMap((f) => f.getFields()),
    // Route writes by field ownership; focus-only forms never accept writes
    setField: (name, value) => {
      for (const f of forms) {
        if (f.focusOnly) continue;
        if (f.setField(name, value)) return true;
      }
      return false;
    },
    focusOnly: forms.every((f) => f.focusOnly === true),
    isDirty: () => forms.some((f) => f.isDirty?.() === true),
  };
}

export function AiFormBridgeProvider({ children }: { children: ReactNode }) {
  const formsRef = useRef<AiFormRegistration[]>([]);
  const dialogsRef = useRef<AiDialogRegistration[]>([]);

  const value = useMemo<AiFormBridgeValue>(
    () => ({
      registerForm: (registration) => {
        formsRef.current = [...formsRef.current, registration];
        return () => {
          formsRef.current = formsRef.current.filter((r) => r !== registration);
        };
      },
      getActiveForm: () => {
        const forms = formsRef.current;
        if (forms.length === 0) return null;
        return forms.length === 1 ? forms[0] : mergeRegistrations(forms);
      },
      registerDialog: (registration) => {
        dialogsRef.current = [...dialogsRef.current, registration];
        return () => {
          dialogsRef.current = dialogsRef.current.filter((r) => r !== registration);
        };
      },
      getDialogs: () => dialogsRef.current,
    }),
    []
  );

  return <AiFormBridgeContext.Provider value={value}>{children}</AiFormBridgeContext.Provider>;
}

export function useAiFormBridge(): AiFormBridgeValue {
  return useContext(AiFormBridgeContext);
}

/**
 * Find the actual control inside a tagged wrapper. Real inputs win; buttons
 * are a fallback for Listbox-style selects — but never buttons inside a
 * <label>, which are tooltip triggers, not the field control.
 */
function findFieldControl(host: HTMLElement): HTMLElement {
  if (host.matches('input, select, textarea, button')) return host;
  const input = host.querySelector<HTMLElement>('input, select, textarea');
  if (input) return input;
  for (const button of host.querySelectorAll<HTMLElement>('button')) {
    if (!button.closest('label')) return button;
  }
  return host;
}

/**
 * Scroll to, focus and briefly highlight the form field tagged with
 * data-ai-field={name} (the attribute sits on the field's wrapper or on the
 * input itself). Returns false when the field is not in the DOM — e.g. its
 * section is still collapsed behind an "+ Add" button.
 *
 * The highlight is the `ai-field-highlight` CSS animation (globals.css);
 * the class removes itself on animationend so repeated calls restart it.
 */
export function focusAiField(name: string): boolean {
  const host = document.querySelector(`[data-ai-field="${CSS.escape(name)}"]`);
  if (!(host instanceof HTMLElement)) return false;
  const target = findFieldControl(host);
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target.focus({ preventScroll: true });
  target.classList.remove('ai-field-highlight');
  void target.offsetWidth; // reflow so re-adding the class restarts the animation
  target.classList.add('ai-field-highlight');
  target.addEventListener('animationend', () => target.classList.remove('ai-field-highlight'), {
    once: true,
  });
  return true;
}

/** Milliseconds between typewriter steps. */
const TYPE_STEP_MS = 45;
/** Cap the number of animation steps so long values stay snappy. */
const MAX_STEPS = 24;

/**
 * Fill a field with a visible typewriter animation.
 *
 * Numbers are typed digit by digit (7 → 70 → 700); text is typed character
 * by character. Dates and selects are set in one step (partial values are
 * invalid). Resolves true when the field was filled, false for unknown fields.
 */
export async function typewriterFill(
  form: AiFormRegistration,
  name: string,
  value: string | number,
  fieldType: AiFormFieldDescriptor['type']
): Promise<boolean> {
  if (fieldType === 'date' || fieldType === 'select') {
    return form.setField(name, value);
  }

  const text = String(value);
  const steps = Math.min(text.length, MAX_STEPS);
  // With capped steps, type in chunks so the final value is always complete
  const chunk = Math.ceil(text.length / steps);

  for (let i = chunk; i < text.length; i += chunk) {
    const partial = text.slice(0, i);
    const partialValue =
      fieldType === 'number' ? (Number.isNaN(Number(partial)) ? null : Number(partial)) : partial;
    if (partialValue !== null) {
      if (!form.setField(name, partialValue)) return false;
      await new Promise((resolve) => setTimeout(resolve, TYPE_STEP_MS));
    }
  }
  return form.setField(name, value);
}
